import React, { useCallback, useEffect, useRef } from 'react';
import CommonBubble from '@home/components/CommonBubble';
import { getName } from '@home/utils/common';
import SvgIcon from '@home/components/SvgIcon';
import BasicImage from '@home/components/BasicImage';
import DatabarinSvg from '@/pages/home/assets/svg/dataSource/databarin.svg';
import { LANGUAGE_EN } from '@home/_config/config';
import { useIntl, useSelector } from 'umi';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import {
  KEY_PERFORMANCE_BENCHMARK_POPOVER_CONFIG,
  type IKeyPerformanceBenchmarkPopoverConfig,
} from '@/pages/dashboard/pages/overview/_config/keyPerformanceBenchmark';
import './KeyPerformanceBenchmarkPopover.scss';

interface IKeyPerformanceBenchmarkPopoverProps {
  config?: IKeyPerformanceBenchmarkPopoverConfig;
  /** 当前卡片 metrics_name，写入埋点 extInfo 便于区分卡片 */
  metricsName?: string;
}

/** useSelector 所需的最小 dashboard 切片（埋点 uid / gameName） */
interface IKeyPerformanceBenchmarkSelectorState {
  dashboard?: {
    currentGame?: {
      game_code?: string;
      game_name?: string;
      game_cn_name?: string;
    };
  };
}


const DEFAULT_POPOVER_CONFIG = KEY_PERFORMANCE_BENCHMARK_POPOVER_CONFIG;

/** 浮层展示满该时长后再上报埋点（与 VsBenchmarkBtn tracking 思路一致） */
const BENCHMARK_BURIED_IMPRESSION_DELAY_MS = 3000;

/**
 * 鼠标从锚点移到浮窗时，tea 会先 false 再 true；延迟该时长再视为「真正收起」，避免打断 3s 曝光计时、也避免二次打点
 */
const BENCHMARK_HIDE_DEBOUNCE_MS = 500;

/** 配置里用 `**片段**` 标识加粗（与 Markdown 习惯一致） */
const KP_BENCHMARK_BOLD_MARKER = /\*\*(.+?)\*\*/;

/**
 * 将带 `**...**` 标记的文案拆成普通文本 + 加粗节点
 */
function renderKpBenchmarkMarkedBold(text: string): React.ReactNode {
  const segments = text.split(KP_BENCHMARK_BOLD_MARKER);
  return (
    <>
      {segments.map((segment, index) =>
        index % 2 === 1 ? (
          <strong
            key={index}
            className="dbd-kp-benchmark-popover__marked-bold"
          >
            {segment}
          </strong>
        ) : (
          <span key={index}>{segment}</span>
        ),
      )}
    </>
  );
}

export function KeyPerformanceBenchmarkPopoverContent(props: {
  config?: IKeyPerformanceBenchmarkPopoverConfig;
}): React.ReactElement {
  const { config: configProp } = props;
  const cfg = configProp ?? DEFAULT_POPOVER_CONFIG;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const title = getName(isEn, cfg.titleEn, cfg.titleCn);
  const colMetrics = getName(isEn, cfg.columnMetricsEn, cfg.columnMetricsCn);
  const colTarget = getName(isEn, cfg.columnTargetEn, cfg.columnTargetCn);
  const footer = getName(isEn, cfg.footerEn, cfg.footerCn);
  const contactLine = getName(
    isEn,
    cfg.footerContactLineEn,
    cfg.footerContactLineCn,
  );
  const moreText = getName(
    isEn,
    cfg.moreTextEn ?? 'More',
    cfg.moreTextCn ?? '更多',
  );

  const jumpToLink = (): void => {
    if (cfg.jumpUrl) {
      window.open(cfg.jumpUrl);
    }
  };

  return (
    <div
      className="dbd-kp-benchmark-popover"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <div className="dbd-kp-benchmark-popover__header">
        <div className="dbd-kp-benchmark-popover__title">
          <BasicImage
            src={DatabarinSvg}
            style={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
            }}
          />
          <span>{title}</span>
        </div>
        {cfg.jumpUrl ? (
          <button
            type="button"
            className="dbd-kp-benchmark-popover__more"
            onClick={jumpToLink}
          >
            {moreText}
            <SvgIcon type="link-to" />
          </button>
        ) : null}
      </div>

      <div className="dbd-kp-benchmark-popover__th">
        <span>{colMetrics}</span>
        <span>{colTarget}</span>
      </div>

      {cfg.rows.map((row, rowIndex) => (
        <div
          className="dbd-kp-benchmark-popover__tr"
          key={`${row.nameEn}-${row.nameCn}-${rowIndex}`}
        >
          <div className="dbd-kp-benchmark-popover__metric">
            <span
              className="dbd-kp-benchmark-popover__dot"
              style={{ background: row.color }}
            />
            <span>{getName(isEn, row.nameEn, row.nameCn)}</span>
          </div>
          <div className="dbd-kp-benchmark-popover__target">
            {getName(isEn, row.targetEn, row.targetCn)}
          </div>
        </div>
      ))}

      <div className="dbd-kp-benchmark-popover__line" />

      <div className="dbd-kp-benchmark-popover__footer">
        <p>{footer}</p>
        <p>{renderKpBenchmarkMarkedBold(contactLine)}</p>
      </div>
    </div>
  );
}

/**
 * Key Performance 卡片上的行业基准入口：蓝色 pill + 暗色浮窗（hover）
 */
export default function KeyPerformanceBenchmarkPopover(
  props: IKeyPerformanceBenchmarkPopoverProps,
): React.ReactElement | null {
  const { config, metricsName } = props;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const cfg = config ?? DEFAULT_POPOVER_CONFIG;

  const btnText = getName(isEn, cfg.buttonTextEn, cfg.buttonTextCn);

  const { currentGame } = useSelector(
    (state: IKeyPerformanceBenchmarkSelectorState) => ({
      currentGame: state.dashboard?.currentGame,
    }),
  );

  const buriedImpressionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const hideDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** 本会话内已成功上报曝光，在真正收起前不再开第二个 3s 计时器（避免按钮→浮窗再打一枪） */
  const hasReportedImpressionRef = useRef(false);

  const clearBuriedImpressionTimer = useCallback((): void => {
    if (buriedImpressionTimerRef.current) {
      clearTimeout(buriedImpressionTimerRef.current);
      buriedImpressionTimerRef.current = null;
    }
  }, []);

  const clearHideDebounceTimer = useCallback((): void => {
    if (hideDebounceTimerRef.current) {
      clearTimeout(hideDebounceTimerRef.current);
      hideDebounceTimerRef.current = null;
    }
  }, []);

  const clearAllBenchmarkTimers = useCallback((): void => {
    clearBuriedImpressionTimer();
    clearHideDebounceTimer();
  }, [clearBuriedImpressionTimer, clearHideDebounceTimer]);

  useEffect(() => {
    return () => {
      clearAllBenchmarkTimers();
    };
  }, [clearAllBenchmarkTimers]);

  const handleBubbleVisibleChange = useCallback(
    (visible: boolean): void => {
      const log = cfg.buriedLog;
      if (!log?.pageId || !log?.buttonId) {
        return;
      }

      const baseExt = log.extInfo ?? '';
      const extInfo = metricsName
        ? `${baseExt}:${metricsName}`
        : baseExt;
      const uid = currentGame?.game_code || '';
      const gameName = currentGame?.game_name || currentGame?.game_cn_name || '';

      if (visible) {
        clearHideDebounceTimer();

        if (hasReportedImpressionRef.current) {
          return;
        }

        if (buriedImpressionTimerRef.current) {
          return;
        }

        buriedImpressionTimerRef.current = setTimeout(() => {
          buriedImpressionTimerRef.current = null;
          if (hasReportedImpressionRef.current) {
            return;
          }
          hasReportedImpressionRef.current = true;
          buriedBtnPoint({
            pageId: log.pageId,
            buttonId: log.buttonId,
            extInfo,
            buttonName: log.buttonName ?? 'KeyPerformanceBenchmarkPopover',
            uidType: 'game_code',
            uid,
            gameName,
            typeId: log.typeId ?? 'dashboard_pc_v2',
          });
        }, BENCHMARK_BURIED_IMPRESSION_DELAY_MS);
        return;
      }

      hideDebounceTimerRef.current = setTimeout(() => {
        hideDebounceTimerRef.current = null;
        clearBuriedImpressionTimer();
        hasReportedImpressionRef.current = false;
      }, BENCHMARK_HIDE_DEBOUNCE_MS);
    },
    [
      cfg.buriedLog,
      clearBuriedImpressionTimer,
      clearHideDebounceTimer,
      currentGame,
      metricsName,
    ],
  );

  return (
    <CommonBubble
      className="dbd-overlay-card-right-bubble-container dbd-kp-benchmark-popover-bubble-wrap"
      dark
      placement="bottom-end"
      trigger="hover"
      content={<KeyPerformanceBenchmarkPopoverContent config={cfg} />}
      onVisibleChange={handleBubbleVisibleChange}
    >
      <div className="dbd-overlay-card-container dbd-kp-benchmark-popover-trigger-inner">
        <div
          className="dbd-overlay-card-right-solt"
          role="button"
          tabIndex={0}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <SvgIcon type="vs-new" />
          {btnText}
        </div>
      </div>
    </CommonBubble>
  );
}
